import { sql } from "drizzle-orm";
import {
  index,
  integer,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import type { SocialLinks } from "@/lib/contact-links";
import type { MediaAssetSlim } from "@/lib/media";

export type MediaData = MediaAssetSlim;
export type SimpleMediaData = MediaAssetSlim;
export type HiiveLocationInput = {
  label: string;
  address?: string | null;
  latitude: number;
  longitude: number;
  placeId?: string | null;
  sortOrder?: number;
  isPrimary?: boolean;
};

export const HIIVE_STATUS = ["active", "inactive", "archived"] as const;

export type HiiveStatus = (typeof HIIVE_STATUS)[number];

export const hiive = sqliteTable(
  "hiive",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),

    logoImage: text("logo_image", { mode: "json" })
      .$type<SimpleMediaData>()
      .notNull(),
    bannerMedia: text("banner_media", { mode: "json" })
      .$type<MediaData[]>()
      .notNull(),

    description: text("description").notNull(),
    location: text("location").notNull(),

    status: text("status").notNull().$type<HiiveStatus>(),

    // Public contact details. Venues and events carry the same four fields so
    // either can copy the hiive's in one tap — see lib/contact-links.ts.
    contactPhone: text("contact_phone"),
    contactEmail: text("contact_email"),
    websiteUrl: text("website_url"),
    socialLinks: text("social_links", { mode: "json" })
      .$type<SocialLinks>()
      .notNull()
      .default(sql`'{}'`),

    // Inheritance default for new events and venues under this hiive. Purely a
    // convenience for the queen who owns it — never a floor or a cap, and any
    // event or venue may override it. Not a FK (see event.refundPolicyId).
    defaultRefundPolicyId: integer("default_refund_policy_id"),

    // Checkout defaults for everything priced under this hiive — event passes,
    // venue experiences and shop items. These mirror the two authoring choices
    // in a `pricingPolicy` ("is tax included in the advertised price", "who
    // pays the Hiive fee") so an organiser sets them once here instead of on
    // every item. Never a floor or a cap: an item overrides them by storing its
    // own concrete `pricingPolicy`, and a policy field of "inherit" follows the
    // hiive. Not a FK.
    defaultTaxDisplayMode: text("default_tax_display_mode")
      .notNull()
      .$type<"included" | "add_at_checkout">()
      .default("included"),
    defaultFeeFundingMode: text("default_fee_funding_mode")
      .notNull()
      .$type<"organiser" | "buyer">()
      .default("organiser")
      .$defaultFn(() => "buyer" as const),

    ...timestamps,
  },
  (table) => [uniqueIndex("hiive_slug_unique").on(table.slug)],
);

export const hiiveLocation = sqliteTable(
  "hiive_location",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),
    label: text("label").notNull(),
    address: text("address"),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    placeId: text("place_id"),
    sortOrder: integer("sort_order").notNull().default(0),
    isPrimary: integer("is_primary", { mode: "boolean" })
      .notNull()
      .default(false),
    geohash: text("geohash").notNull(),
    ...timestamps,
  },
  (table) => [
    index("hiive_location_by_hiive").on(table.hiiveId),
    index("hiive_location_by_hiive_primary").on(table.hiiveId, table.isPrimary),
    index("hiive_location_by_hiive_sort").on(table.hiiveId, table.sortOrder),
    index("hiive_location_geohash_idx").on(table.geohash),
  ],
);
